import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { GpsReadingsTable, type SubmissionGps } from './ReviewPanels.js';

/**
 * M10 L10.05 — GPS Validation (§11).
 *
 * Lists the positions the painter's phone recorded for the review's job
 * (`GET /v1/quality/reviews/:reviewId/gps`), each set by the server against the
 * job's site with the tenant's radius and accuracy limits, and the decision
 * recorded on it. The reviewer names a reading and decides via
 * `POST /v1/quality/reviews/:reviewId/gps`; no position is typed by hand. An
 * override reason is required when the reviewer overrides.
 */
type GpsDecision = 'APPROVED' | 'REJECTED' | 'OVERRIDE';

export function QualityGpsValidationPage(): ReactNode {
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const readingLabel = (g: SubmissionGps): string => [g.source, at(g.capturedAt)].join(' · ');
  const gpsPath = `/v1/quality/reviews/${reviewId}/gps`;
  const [rows, setRows] = useState<readonly SubmissionGps[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [gpsId, setGpsId] = useState('');
  const [decision, setDecision] = useState<GpsDecision>('APPROVED');
  const [overrideReason, setOverrideReason] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<readonly SubmissionGps[]>(gpsPath, { token });
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [gpsPath, token]);

  useEffect(() => { void load(); }, [load]);

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(gpsPath, {
        method: 'POST', token,
        body: {
          gpsId: gpsId.trim(),
          decision,
          overrideReason: decision === 'OVERRIDE' ? overrideReason.trim() : undefined,
        },
      });
      setGpsId(''); setDecision('APPROVED'); setOverrideReason('');
      await load();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [gpsPath, token, gpsId, decision, overrideReason, load]);

  const overrideMissing = decision === 'OVERRIDE' && !overrideReason.trim();

  return (
    <main aria-label="quality-gps-validation">
      <p style={{ marginBottom: 8 }}><Link to={`/quality/review/${reviewId}`}>{t('quality.common.back')}</Link></p>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.gps.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <form onSubmit={onSubmit} aria-label="gps-decision-form" style={{ maxWidth: 520, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <RecordPicker<SubmissionGps>
          endpoint={gpsPath}
          idField="gpsId"
          label={t('quality.gps.reading')}
          testId="gps-id"
          value={gpsId}
          optionLabel={readingLabel}
          onPick={setGpsId}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="gps-decision" style={{ fontSize: 12 }}>{t('quality.common.decision')}</label>
          <select id="gps-decision" data-testid="gps-decision" value={decision}
                  onChange={(e) => setDecision(e.target.value as GpsDecision)}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
            <option value="APPROVED">{t('quality.common.approve')}</option>
            <option value="REJECTED">{t('quality.common.reject')}</option>
            <option value="OVERRIDE">{t('quality.gps.opt.override')}</option>
          </select>
        </div>
        {decision === 'OVERRIDE' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="gps-override" style={{ fontSize: 12 }}>{t('quality.gps.override')}</label>
            <textarea id="gps-override" data-testid="gps-override" value={overrideReason} rows={2}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
          </div>
        )}
        <div>
          <Button type="submit" data-testid="gps-submit" disabled={busy || !gpsId.trim() || overrideMissing} variant="primary">
            {busy ? t('quality.common.saving') : t('quality.common.save')}
          </Button>
          {actionError && <p role="alert" style={{ color: 'var(--color-danger)', marginTop: 8 }}>{actionError}</p>}
        </div>
      </form>

      <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.gps.recorded')}</h2>
      <GpsReadingsTable rows={rows} />
    </main>
  );
}